/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from '../common/generic-dialog/generic-dialog.component';
import * as strings from '@touchplan/strings';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { OrganizationService } from '../modules/organizations/services/organization.service';
import { Subject } from 'rxjs';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { HttpErrorResponse } from '@angular/common/http';
import { fbReporting } from '../plain-javascript/background-scripts/fb-reporting';

@Injectable()
export class OrganizationDisclaimerService implements OnDestroy {
	private shutdownSubject$: Subject<void> = new Subject();
	private logger = LoggerServiceFactory.create('OrganizationDisclaimerService');
	constructor(public dialog: MatDialog, private organizationService: OrganizationService) {}

	private openDisclaimerDialog(projectId: string, userId: string, planId: string) {
		const disclaimerDialogRef = this.dialog.open(GenericDialogComponent, {
			data: {
				content: strings.ORG_DISCLAIMER_TEXT,
				yesButtonText: strings.ORG_DISCLAIMER_ACCEPT_BUTTON,
			},
			disableClose: true,
			width: '400px',
		});
		disclaimerDialogRef
			.afterClosed()
			.pipe(
				take(1),
				// There is no need to check the result of the dialog, we assume that the user has accepted the disclaimer by closing the dialog
				switchMap(() => this.organizationService.updateUserOrgsScheduleDisclaimer$(projectId, userId, planId))
			)
			.subscribe();
	}

	/**
	 * Subscribes to the organization schedule disclaimer status and shows the disclaimer dialog if needed.
	 *
	 * @param projectId - The ID of the project.
	 * @param userId - The ID of the user.
	 * @param planId - The ID of the plan.
	 */
	public subscribeAndShowOrganizationDisclaimer(projectId: string, userId: string, planId: string) {
		this.organizationService
			.getIsOrgScheduleDisclaimerNeededAndExpired$(projectId, userId)
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe({
				next: (isOrgScheduleDisclaimerNeeded) => {
					if (isOrgScheduleDisclaimerNeeded) {
						this.openDisclaimerDialog(projectId, userId, planId);
					}
				},
				error: (error) => {
					if ((error as HttpErrorResponse)?.status === 0) {
						this.logger.error('Organization api request failed, api probably not running', error);
					} else {
						this.logger.error('Unknown error while checking organization schedule disclaimer status', error);
					}
				},
			});
	}

	ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
